import { StaffCalendarAccess } from '../components/StaffCalendarAccess';
import { useEffect, useState } from 'react';
import type { Calendar } from '../../shared/contracts';
import { api, type CalendarSettings, type ClientSession, type Preferences } from '../api';
import { ErrorMessage, Loading } from '../components/Status';

const defaultPreferences: Preferences = {
  view: 'month',
  timezone: 'America/Los_Angeles',
  theme: 'system',
  calendarIds: [],
};

function EditableCalendar({
  calendar,
  session,
  onSaved,
}: {
  calendar: Calendar;
  session: ClientSession;
  onSaved: (calendar: Calendar) => void;
}) {
  const [values, setValues] = useState<CalendarSettings>({
    name: calendar.name,
    description: calendar.description,
    color: calendar.color,
    visibility: calendar.visibility,
    staffOnly: !!calendar.staffOnly,
    staffAccess: calendar.staffAccess || (calendar.staffOnly ? 'selected' : 'all'),
    staffDefaultRole: calendar.staffDefaultRole || 'viewer',
  });
  const [status, setStatus] = useState('');
  const [publishConfirmed, setPublishConfirmed] = useState(false);
  const publishingPrivate = calendar.visibility === 'private' && values.visibility === 'public';
  const save = async () => {
    setStatus('Saving…');
    try {
      const result = await api.updateCalendar(
        calendar.id,
        {
          ...values,
          ...(publishingPrivate ? { confirmPublishPrivate: publishConfirmed } : {}),
        },
        session.csrfToken,
      );
      onSaved(result.calendar);
      setStatus('Saved');
    } catch (cause) {
      setStatus(cause instanceof Error ? cause.message : 'Could not save');
    }
  };
  return (
    <details className="settings-calendar">
      <summary>
        <i style={{ background: values.color }} />
        <span>
          <b>{values.name}</b>
          <small>{values.staffOnly ? 'Staff only' : values.visibility}</small>
        </span>
        <span>Edit</span>
      </summary>
      <div className="settings-calendar-form">
        <label className="field">
          <span>Name</span>
          <input
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Description</span>
          <input
            value={values.description || ''}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
          />
        </label>
        <div className="form-grid">
          <label className="field">
            <span>Color</span>
            <input
              type="color"
              value={values.color || '#296b62'}
              onChange={(e) => setValues({ ...values, color: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Visibility</span>
            <select
              value={values.staffOnly ? 'staff' : values.visibility}
              onChange={(e) => {
                setPublishConfirmed(false);
                setValues({
                  ...values,
                  visibility: e.target.value === 'public' ? 'public' : 'private',
                  staffOnly: e.target.value === 'staff',
                  ...(!values.staffOnly && e.target.value === 'staff'
                    ? { staffAccess: 'all' as const, staffDefaultRole: 'viewer' as const }
                    : {}),
                });
              }}
            >
              <option value="public">Public</option>
              <option value="private">Members only</option>
              <option value="staff">Staff only (approved domains)</option>
            </select>
          </label>
        </div>
        <StaffCalendarAccess value={values} onChange={setValues} />
        {publishingPrivate && (
          <label className="settings-warning check-field">
            <input
              type="checkbox"
              checked={publishConfirmed}
              onChange={(event) => setPublishConfirmed(event.target.checked)}
            />
            I understand this calendar and its published events will become public.
          </label>
        )}
        <div className="settings-save">
          <button
            className="button button-small"
            disabled={publishingPrivate && !publishConfirmed}
            onClick={() => void save()}
          >
            Save calendar
          </button>
          <span role="status">{status}</span>
        </div>
      </div>
    </details>
  );
}

export function SettingsPage({
  session,
  calendars,
  onCalendarsChanged,
}: {
  session: ClientSession | null;
  calendars: Calendar[];
  onCalendarsChanged: (calendars: Calendar[]) => void;
}) {
  const [preferences, setPreferences] = useState<Preferences>();
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [newCalendar, setNewCalendar] = useState<CalendarSettings>({
    name: '',
    description: '',
    color: '#d9693f',
    visibility: 'public',
  });
  useEffect(() => {
    if (session?.user)
      api
        .preferences()
        .then(({ preferences: value }) => setPreferences(value))
        .catch((cause) => setError(cause.message));
  }, [session]);
  if (!session?.user)
    return (
      <main id="main-content" className="narrow-page">
        <div className="empty-state">
          <h1>Your preferences</h1>
          <p>Sign in to manage your preferences.</p>
          <a className="button" href="/login">
            Sign in
          </a>
        </div>
      </main>
    );
  if (error && !preferences)
    return (
      <main id="main-content" className="narrow-page">
        <ErrorMessage message={error} />
      </main>
    );
  if (!preferences)
    return (
      <main id="main-content" className="narrow-page">
        <Loading label="Loading settings" />
      </main>
    );
  const savePreferences = async () => {
    setStatus('Saving…');
    setError('');
    try {
      const result = await api.updatePreferences(preferences, session.csrfToken);
      setPreferences(result.preferences);
      const view = {
        month: 'dayGridMonth',
        week: 'timeGridWeek',
        day: 'timeGridDay',
        list: 'listMonth',
      }[result.preferences.view];
      localStorage.setItem('dragonfly-calendar-view', view);
      localStorage.setItem('dragonfly-calendar-ids', result.preferences.calendarIds.join(','));
      document.documentElement.dataset.theme =
        result.preferences.theme === 'system' ? '' : result.preferences.theme;
      setStatus('Saved');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save settings');
      setStatus('');
    }
  };
  const create = async () => {
    setStatus('Creating…');
    try {
      const result = await api.createCalendar(newCalendar, session.csrfToken);
      onCalendarsChanged([...calendars, result.calendar]);
      setNewCalendar({ ...newCalendar, name: '', description: '' });
      setAdding(false);
      setStatus('Calendar created');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create calendar');
      setStatus('');
    }
  };
  return (
    <main id="main-content" className="settings-page">
      <a className="back-link" href="/">
        ← Back to calendar
      </a>
      <span className="eyebrow">Your workspace</span>
      <h1>Settings</h1>
      {calendars.some((calendar) => calendar.canEdit && calendar.source === 'local') && (
        <p>
          <a className="button" href="/import">
            Import a calendar file
          </a>
        </p>
      )}
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      <section className="settings-card">
        <h2>Calendar preferences</h2>
        <p>Choose how the calendar opens when you sign in.</p>
        <div className="form-grid">
          <label className="field">
            <span>Default view</span>
            <select
              value={preferences.view}
              onChange={(e) =>
                setPreferences({ ...preferences, view: e.target.value as Preferences['view'] })
              }
            >
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="day">Day</option>
              <option value="list">List</option>
            </select>
          </label>
          <label className="field">
            <span>Appearance</span>
            <select
              value={preferences.theme}
              onChange={(e) =>
                setPreferences({ ...preferences, theme: e.target.value as Preferences['theme'] })
              }
            >
              <option value="system">Use device setting</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>
        </div>
        <fieldset>
          <legend>Calendars shown by default</legend>
          {calendars.map((calendar) => (
            <label className="check-field" key={calendar.id}>
              <input
                type="checkbox"
                checked={preferences.calendarIds.includes(calendar.id)}
                onChange={(e) =>
                  setPreferences({
                    ...preferences,
                    calendarIds: e.target.checked
                      ? [...preferences.calendarIds, calendar.id]
                      : preferences.calendarIds.filter((id) => id !== calendar.id),
                  })
                }
              />
              {calendar.name}
            </label>
          ))}
        </fieldset>
        <div className="settings-save">
          <button className="button" onClick={() => void savePreferences()}>
            Save preferences
          </button>
          <span role="status">{status}</span>
        </div>
      </section>
      {(session.user.role === 'admin' || session.capabilities.superAdmin) && (
        <section className="settings-card">
          <div className="settings-title-row">
            <div>
              <h2>Calendars</h2>
              <p>Create calendars and control who can see them.</p>
            </div>
            <button className="button button-small" onClick={() => setAdding(!adding)}>
              {adding ? 'Cancel' : 'New calendar'}
            </button>
          </div>
          {adding && (
            <div className="new-calendar-form">
              <div className="form-grid">
                <label className="field">
                  <span>Name</span>
                  <input
                    autoFocus
                    value={newCalendar.name}
                    onChange={(e) => setNewCalendar({ ...newCalendar, name: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Description</span>
                  <input
                    value={newCalendar.description || ''}
                    onChange={(e) =>
                      setNewCalendar({ ...newCalendar, description: e.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Color</span>
                  <input
                    type="color"
                    value={newCalendar.color}
                    onChange={(e) => setNewCalendar({ ...newCalendar, color: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Visibility</span>
                  <select
                    value={newCalendar.staffOnly ? 'staff' : newCalendar.visibility}
                    onChange={(e) =>
                      setNewCalendar({
                        ...newCalendar,
                        visibility: e.target.value === 'public' ? 'public' : 'private',
                        staffOnly: e.target.value === 'staff',
                      })
                    }
                  >
                    <option value="public">Public</option>
                    <option value="private">Members only</option>
                    <option value="staff">Staff only (approved domains)</option>
                  </select>
                </label>
              </div>
              <StaffCalendarAccess value={newCalendar} onChange={setNewCalendar} />
              <button
                className="button button-small"
                disabled={!newCalendar.name.trim()}
                onClick={() => void create()}
              >
                Create calendar
              </button>
            </div>
          )}
          <div className="settings-calendar-list">
            {calendars.map((calendar) => (
              <EditableCalendar
                key={calendar.id}
                calendar={calendar}
                session={session}
                onSaved={(saved) =>
                  onCalendarsChanged(calendars.map((item) => (item.id === saved.id ? saved : item)))
                }
              />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
